/**
 * @engine/core - LevelManager
 *
 * Manages level loading, unloading, and switching.
 * Provides global level access and lifecycle.
 *
 * Note: Renamed from SceneManager to LevelManager.
 */

import { Level } from "./Level";
import type { LevelId, GUID, GameObjectId } from "./types";
import { GameObject } from "./GameObject";

/**
 * Level load mode
 */
export enum LoadLevelMode {
  /** Replace all loaded levels */
  Single = 0,
  /** Add to currently loaded levels */
  Additive = 1,
}

/**
 * Level load request for async loading
 */
export interface LevelLoadRequest {
  guid: GUID;
  mode: LoadLevelMode;
  onLoaded?: (level: Level) => void;
  onError?: (error: Error) => void;
}

/**
 * Host that actually drives level loading (deserialize, acquire assets, wire subsystems).
 * Registered by the editor/player via {@link LevelManager.setLoaderHost}; the core manager stays
 * host-agnostic and only delegates. `load` is required; `preload`/`unload`/`progress` are optional
 * (the player provides all; the editor provides `load`). The scripting `engine.level` API routes
 * here.
 */
export interface LevelLoaderHost {
  load(guid: GUID, mode: LoadLevelMode): Promise<Level>;
  preload?(guid: GUID): Promise<void>;
  unload?(guid: GUID): Promise<void>;
  progress?(guid: GUID): number;
}

/** Payloads for the global level-lifecycle events (see {@link LevelManager.events}). */
export interface LevelEventMap {
  loadStart: { guid: GUID };
  progress: { guid: GUID; progress: number };
  loaded: { guid: GUID };
  activated: { guid: GUID; mode: LoadLevelMode };
  unloaded: { guid: GUID };
  error: { guid: GUID; error: Error };
}

type LevelEventListener<K extends keyof LevelEventMap> = (payload: LevelEventMap[K]) => void;

/** Tiny typed emitter for global level-lifecycle events. */
export class LevelEvents {
  private readonly _listeners: { [K in keyof LevelEventMap]: Set<LevelEventListener<K>> } = {
    loadStart: new Set(),
    progress: new Set(),
    loaded: new Set(),
    activated: new Set(),
    unloaded: new Set(),
    error: new Set(),
  };

  on<K extends keyof LevelEventMap>(event: K, listener: LevelEventListener<K>): () => void {
    (this._listeners[event] as Set<LevelEventListener<K>>).add(listener);
    return () => {
      (this._listeners[event] as Set<LevelEventListener<K>>).delete(listener);
    };
  }

  emit<K extends keyof LevelEventMap>(event: K, payload: LevelEventMap[K]): void {
    for (const listener of this._listeners[event] as Set<LevelEventListener<K>>) {
      try {
        listener(payload);
      } catch (e) {
        console.error(`[LevelManager] '${event}' listener threw:`, e);
      }
    }
  }

  clear(): void {
    for (const set of Object.values(this._listeners)) (set as Set<unknown>).clear();
  }
}

/**
 * LevelManager - Global level management
 *
 * Features:
 * - Multiple loaded levels (additive loading)
 * - Active level for new object creation
 * - Level switching
 * - Don't Destroy On Load support
 *
 * @example
 * ```typescript
 * const manager = LevelManager.instance;
 *
 * // Create a new level
 * const level = manager.createLevel('MainLevel');
 *
 * // Load level additively
 * await manager.loadLevel(levelGuid, LoadLevelMode.Additive);
 *
 * // Mark object as persistent
 * manager.dontDestroyOnLoad(player);
 * ```
 */
export class LevelManager {
  // Singleton
  private static _instance: LevelManager | null = null;

  // Loaded levels
  private _levels: Map<LevelId, Level> = new Map();
  private _levelsByGuid: Map<GUID, Level> = new Map();

  // Active level (where new objects are created)
  private _activeLevel: Level | null = null;

  // Objects that persist across level loads
  private _dontDestroyOnLoad: Set<GameObjectId> = new Set();
  private _dontDestroyLevel: Level | null = null;

  // Load queue
  private _loadQueue: LevelLoadRequest[] = [];
  private _isLoading: boolean = false;

  // Host (registered by editor/player); the manager only delegates.
  private _loaderHost: LevelLoaderHost | null = null;

  // Global level-lifecycle events
  private readonly _events = new LevelEvents();

  private constructor() {
    // Create DontDestroyOnLoad level
    this._dontDestroyLevel = new Level({ name: "DontDestroyOnLoad" });
    this._levels.set(this._dontDestroyLevel.id, this._dontDestroyLevel);
  }

  /** Global level-lifecycle event bus (loadStart/progress/loaded/activated/unloaded/error). */
  get events(): LevelEvents {
    return this._events;
  }

  /**
   * Register the host that actually loads/preloads/unloads levels. The editor and player each
   * register their own. Without a host, {@link loadLevel}/{@link preloadLevel}/{@link unloadLevelById}
   * reject. The scripting `engine.level.*` API routes through these.
   */
  setLoaderHost(host: LevelLoaderHost | null): void {
    this._loaderHost = host;
  }

  /**
   * Preload a level (background: load + acquire assets WITHOUT activating). Delegates to the host's
   * optional `preload`; rejects if the host doesn't support it. Used by `engine.level.preload`.
   */
  preloadLevel(guid: GUID): Promise<void> {
    if (!this._loaderHost?.preload) {
      return Promise.reject(new Error("LevelManager: registered host does not support preload"));
    }
    this._events.emit("loadStart", { guid });
    return this._loaderHost.preload(guid).then(
      () => {
        this._events.emit("loaded", { guid });
      },
      (err) => {
        this._events.emit("error", { guid, error: err as Error });
        throw err;
      },
    );
  }

  /** Preload progress for a level (0..1). Delegates to the host's optional `progress` (else 0). */
  loadProgress(guid: GUID): number {
    return this._loaderHost?.progress?.(guid) ?? 0;
  }

  /**
   * Unload a level by GUID. Prefers the host's optional `unload` (full subsystem teardown +
   * asset release); falls back to {@link unloadLevel} on the registered level. Used by
   * `engine.level.unload`.
   */
  unloadLevelById(guid: GUID): Promise<void> {
    if (this._loaderHost?.unload) {
      return this._loaderHost.unload(guid);
    }
    const level = this.getLevelByGuid(guid);
    if (level) this.unloadLevel(level);
    return Promise.resolve();
  }

  /**
   * Get LevelManager instance
   */
  static get instance(): LevelManager {
    if (!LevelManager._instance) {
      LevelManager._instance = new LevelManager();
    }
    return LevelManager._instance;
  }

  /**
   * Reset singleton (for testing)
   */
  static reset(): void {
    LevelManager._instance = null;
  }

  // ==========================================================================
  // ACTIVE LEVEL
  // ==========================================================================

  /**
   * Get the active level
   */
  get activeLevel(): Level | null {
    return this._activeLevel;
  }

  /**
   * Set the active level
   */
  set activeLevel(level: Level | null) {
    if (level && !this._levels.has(level.id)) {
      throw new Error(`Level '${level.name}' is not loaded`);
    }
    this._activeLevel = level;
  }

  /**
   * Get number of loaded levels (excluding DontDestroyOnLoad)
   */
  get levelCount(): number {
    return this._levels.size - 1; // Exclude DontDestroyOnLoad
  }

  // ==========================================================================
  // LEVEL CREATION
  // ==========================================================================

  /**
   * Create a new empty level
   */
  createLevel(name?: string): Level {
    const level = new Level(name !== undefined ? { name } : {});
    this._levels.set(level.id, level);
    this._levelsByGuid.set(level.guid, level);

    // If no active level, make this active
    if (!this._activeLevel) {
      this._activeLevel = level;
    }

    return level;
  }

  /**
   * Register an externally-created level (e.g. deserialized from JSON)
   * so that it can be set as the active level.
   */
  registerLevel(level: Level): void {
    if (this._levels.has(level.id)) return;
    this._levels.set(level.id, level);
    this._levelsByGuid.set(level.guid, level);
  }

  /**
   * Remove a level from the manager without clearing/destroying it.
   * Used when the editor replaces the current level (e.g. play-mode restore).
   */
  unregisterLevel(level: Level): void {
    if (level === this._dontDestroyLevel) return;
    this._levels.delete(level.id);
    this._levelsByGuid.delete(level.guid);
    if (this._activeLevel === level) {
      this._activeLevel = null;
    }
  }

  /**
   * Load level by GUID (async via VFS)
   */
  loadLevel(
    guid: GUID,
    mode: LoadLevelMode = LoadLevelMode.Single,
  ): Promise<Level> {
    return new Promise((resolve, reject) => {
      this._loadQueue.push({
        guid,
        mode,
        onLoaded: resolve,
        onError: reject,
      });

      this.processLoadQueue();
    });
  }

  /**
   * Process level load queue by delegating to the registered host loader.
   * The loader owns deserialization, asset acquisition, subsystem wiring and (for Single mode)
   * teardown of the previous level — the manager only sequences requests and emits events.
   */
  private async processLoadQueue(): Promise<void> {
    if (this._isLoading || this._loadQueue.length === 0) return;

    this._isLoading = true;

    while (this._loadQueue.length > 0) {
      const request = this._loadQueue.shift()!;

      try {
        if (!this._loaderHost) {
          throw new Error(
            "LevelManager: no loader host registered. Call levelManager.setLoaderHost(...) from the host (editor / player LevelSubsystem) before loadLevel().",
          );
        }

        this._events.emit("loadStart", { guid: request.guid });
        const level = await this._loaderHost.load(request.guid, request.mode);
        this._events.emit("loaded", { guid: request.guid });
        this._events.emit("activated", { guid: request.guid, mode: request.mode });

        request.onLoaded?.(level);
      } catch (error) {
        this._events.emit("error", { guid: request.guid, error: error as Error });
        request.onError?.(error as Error);
      }
    }

    this._isLoading = false;
  }

  // ==========================================================================
  // LEVEL UNLOADING
  // ==========================================================================

  /**
   * Unload a level
   */
  unloadLevel(level: Level): void {
    if (level === this._dontDestroyLevel) {
      throw new Error("Cannot unload DontDestroyOnLoad level");
    }

    // Clear level
    level.clear();

    // Remove from maps
    this._levels.delete(level.id);
    this._levelsByGuid.delete(level.guid);

    // Update active level
    if (this._activeLevel === level) {
      // Pick first available level
      for (const l of this._levels.values()) {
        if (l !== this._dontDestroyLevel) {
          this._activeLevel = l;
          break;
        }
      }
      if (this._activeLevel === level) {
        this._activeLevel = null;
      }
    }

    this._events.emit("unloaded", { guid: level.guid });
  }

  /**
   * Unload all levels except DontDestroyOnLoad
   */
  unloadAllLevels(): void {
    const toUnload: Level[] = [];

    for (const level of this._levels.values()) {
      if (level !== this._dontDestroyLevel) {
        toUnload.push(level);
      }
    }

    for (const level of toUnload) {
      this.unloadLevel(level);
    }
  }

  // ==========================================================================
  // LEVEL LOOKUP
  // ==========================================================================

  /**
   * Get level by ID
   */
  getLevelById(id: LevelId): Level | null {
    return this._levels.get(id) ?? null;
  }

  /**
   * Get level by GUID
   */
  getLevelByGuid(guid: GUID): Level | null {
    return this._levelsByGuid.get(guid) ?? null;
  }

  /**
   * Get level by name
   */
  getLevelByName(name: string): Level | null {
    for (const level of this._levels.values()) {
      if (level.name === name) return level;
    }
    return null;
  }

  /**
   * Get all loaded levels (excluding DontDestroyOnLoad)
   */
  getAllLevels(): Level[] {
    const result: Level[] = [];
    for (const level of this._levels.values()) {
      if (level !== this._dontDestroyLevel) {
        result.push(level);
      }
    }
    return result;
  }

  /**
   * Iterate ALL loaded levels (including DontDestroyOnLoad).
   * Used by SceneEventsSubsystem to flush events on every level.
   */
  levels(): IterableIterator<Level> {
    return this._levels.values();
  }

  // ==========================================================================
  // DONT DESTROY ON LOAD
  // ==========================================================================

  /**
   * Mark GameObject to persist through level loads
   */
  dontDestroyOnLoad(go: GameObject): void {
    if (!this._dontDestroyLevel) return;

    // Move to DontDestroyOnLoad level
    const currentLevel =
      go.sceneId !== null ? this.getLevelById(go.sceneId) : null;

    if (currentLevel) {
      currentLevel.removeGameObject(go);
    }

    this._dontDestroyLevel.addGameObject(go);
    this._dontDestroyOnLoad.add(go.id);
  }

  /**
   * Check if GameObject is marked as DontDestroyOnLoad
   */
  isDontDestroyOnLoad(go: GameObject): boolean {
    return this._dontDestroyOnLoad.has(go.id);
  }

  // ==========================================================================
  // GLOBAL OBJECT LOOKUP
  // ==========================================================================

  /**
   * Find GameObject by GUID across all levels
   */
  findObjectByGuid(guid: GUID): GameObject | null {
    for (const level of this._levels.values()) {
      const go = level.findByGuid(guid);
      if (go) return go;
    }
    return null;
  }

  /**
   * Find first GameObject with name across all levels
   */
  findObjectByName(name: string): GameObject | null {
    for (const level of this._levels.values()) {
      const go = level.findByName(name);
      if (go) return go;
    }
    return null;
  }

  /**
   * Find all GameObjects with tag across all levels
   */
  findObjectsByTag(tag: string): GameObject[] {
    throw new Error("Tag-based lookup not implemented yet");
    const result: GameObject[] = [];
    for (const level of this._levels.values()) {
      //result.push(...level.findAllByTag(tag));
    }
    return result;
  }
}

// Export singleton accessor
export const levelManager = LevelManager.instance;
